import type {
  AccessChangeResult,
  AccessManagementPerson,
  AdminCoverageDashboard,
  AdminDevelopmentResetResult,
  AdminHouseStreetMatchResult,
  AdminNeighborhoodReview,
  AdminNeighborhoodSummary,
  AdminPublishResult,
  AdminRebuildResult,
  ApiErrorPayload,
  AssignmentSummary,
  AssignmentActivityResetResult,
  ClaimAssignmentRequest,
  HouseConditionStatus,
  HouseInclusionStatus,
  IdentityCandidateResponse,
  IdentitySessionResponse,
  IdentityVerificationStartResponse,
  SelfRegisteredPersonSummary,
  GroupMemberSummary,
  GroupAssignmentReturnResult,
  GroupReservationResult,
  GroupSearchResult,
  LeaderDashboardResponse,
  LeaderAvailableSection,
  NeighborhoodRecheckResult,
  PersonSearchResult,
  PlanningCenterSyncResult,
  PlanningCenterSyncStatus,
  ReviewHouse,
  SeasonActionResult,
  SeasonState,
  SectionReleaseResult,
  TeamMember,
  VolunteerBootstrap,
  VolunteerAvailableSection
} from '../domain/types';
import { MockCoverageApi } from './mock';
import { signalCoverageDataChanged } from '../data-sync';

export class CoverageApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly requestId?: string,
    readonly statusCode?: number
  ) {
    super(`${message}${requestId ? ` Reference: ${requestId}` : ''}`);
    this.name = 'CoverageApiError';
  }
}

export interface CoverageApi {
  findIdentityCandidates(lastFour: string, portal: 'USER' | 'LEADER' | 'ADMIN'): Promise<IdentityCandidateResponse>;
  startIdentityVerification(candidateToken: string, personId: string): Promise<IdentityVerificationStartResponse>;
  startSelfRegistration(candidateToken: string, displayName: string, phoneNumber: string): Promise<IdentityVerificationStartResponse>;
  verifyIdentityCode(verificationToken: string, code: string): Promise<IdentitySessionResponse>;
  getVolunteerBootstrap(): Promise<VolunteerBootstrap>;
  getVolunteerAvailableSections(sessionToken: string, neighborhoodId: string): Promise<VolunteerAvailableSection[]>;
  searchPlanningCenterPeople(sessionToken: string, query: string): Promise<PersonSearchResult[]>;
  getMyAssignments(sessionToken: string): Promise<AssignmentSummary[]>;
  claimAssignment(request: ClaimAssignmentRequest): Promise<AssignmentSummary>;
  getAssignmentSession(claimToken: string): Promise<AssignmentSummary>;
  startAssignmentSection(assignmentId: string, sectionId: string, claimToken: string, teamMembers: TeamMember[]): Promise<AssignmentSummary>;
  completeAssignmentSection(assignmentId: string, sectionId: string, claimToken: string, teamMembers: TeamMember[], completionComment?: string): Promise<AssignmentSummary>;
  reportHouseCondition(assignmentId: string, sectionId: string, houseId: string, claimToken: string, conditionStatus: HouseConditionStatus | null): Promise<AssignmentSummary>;
  getLeaderDashboard(sessionToken: string): Promise<LeaderDashboardResponse>;
  getLeaderCoverage(sessionToken: string): Promise<AdminCoverageDashboard>;
  getLeaderGroupMembers(sessionToken: string, groupId: string): Promise<GroupMemberSummary[]>;
  getLeaderReservedSections(sessionToken: string, groupId: string): Promise<LeaderAvailableSection[]>;
  getLeaderAvailableSections(sessionToken: string, neighborhoodId: string, groupId: string): Promise<LeaderAvailableSection[]>;
  assignLeaderMaps(sessionToken: string, input: { groupId: string; personId: string; neighborhoodId: string; requestedStacks: number; selectedSectionIds: string[]; allowAdditional: boolean }): Promise<AssignmentSummary>;
  releaseLeaderSections(sessionToken: string, assignmentId: string, routeIds: string[]): Promise<SectionReleaseResult>;
  getPlanningCenterStatus(adminKey: string): Promise<PlanningCenterSyncStatus>;
  getSelfRegistrations(adminKey: string): Promise<SelfRegisteredPersonSummary[]>;
  markSelfRegistrationReviewed(adminKey: string, personId: string): Promise<void>;
  syncPlanningCenter(adminKey: string): Promise<PlanningCenterSyncResult>;
  searchAdminPeople(adminKey: string, query: string): Promise<PersonSearchResult[]>;
  searchAdminAccessPeople(adminKey: string, query: string): Promise<AccessManagementPerson[]>;
  setAdminAccess(adminKey: string, personId: string, role: 'LEADER' | 'ADMIN', enabled: boolean): Promise<AccessChangeResult>;
  searchAdminGroups(adminKey: string, query: string): Promise<GroupSearchResult[]>;
  reserveAdminSectionsForGroup(adminKey: string, groupId: string, selectedSectionIds: string[]): Promise<GroupReservationResult>;
  releaseAdminGroupReservations(adminKey: string, routeIds: string[]): Promise<GroupReservationResult>;
  assignAdminMaps(adminKey: string, input: { personId: string; neighborhoodId: string; requestedStacks: number; selectedSectionIds: string[]; allowAdditional: boolean }): Promise<AssignmentSummary>;
  releaseAdminSections(adminKey: string, assignmentId: string, routeIds: string[]): Promise<SectionReleaseResult>;
  returnAdminAssignmentToGroup(adminKey: string, assignmentId: string): Promise<GroupAssignmentReturnResult>;
  completeAdminSection(adminKey: string, sectionId: string, teamMembers: TeamMember[]): Promise<void>;
  reopenAdminUnassignedSection(adminKey: string, sectionId: string): Promise<void>;
  reopenAdminSection(adminKey: string, assignmentId: string, sectionId: string): Promise<AssignmentSummary>;
  getAdminNeighborhoods(adminKey: string): Promise<AdminNeighborhoodSummary[]>;
  setNeighborhoodPlanningGroup(adminKey: string, neighborhoodId: string, planningGroup: 'CURRENT' | 'FUTURE' | 'NOT_IN_SCOPE'): Promise<AdminNeighborhoodSummary>;
  recheckNeighborhood(adminKey: string, neighborhoodId: string): Promise<NeighborhoodRecheckResult>;
  getSeason(adminKey: string): Promise<SeasonState>;
  pauseSeason(adminKey: string): Promise<SeasonActionResult>;
  resumeSeason(adminKey: string): Promise<SeasonActionResult>;
  closeSeason(adminKey: string, confirmation: string): Promise<SeasonActionResult>;
  createSeason(adminKey: string, name: string): Promise<SeasonActionResult>;
  clearAssignmentActivity(adminKey: string, confirmation: string): Promise<AssignmentActivityResetResult>;
  getAdminCoverage(adminKey: string, neighborhoodId?: string): Promise<AdminCoverageDashboard>;
  getAdminReview(adminKey: string, neighborhoodId: string): Promise<AdminNeighborhoodReview>;
  setAdminHouseCondition(adminKey: string, neighborhoodId: string, houseId: string, conditionStatus: HouseConditionStatus | null): Promise<ReviewHouse>;
  setHouseInclusion(
    adminKey: string,
    neighborhoodId: string,
    houseId: string,
    inclusionStatus: HouseInclusionStatus
  ): Promise<ReviewHouse>;
  setHousesInclusion(
    adminKey: string,
    neighborhoodId: string,
    houseIds: string[],
    inclusionStatus: HouseInclusionStatus
  ): Promise<ReviewHouse[]>;
  matchHouseToStreet(
    adminKey: string,
    neighborhoodId: string,
    houseId: string,
    streetUnitId: string,
    method: 'ADMIN_SUGGESTED' | 'ADMIN_SELECTED'
  ): Promise<AdminHouseStreetMatchResult>;
  createManualStreetForHouse(
    adminKey: string,
    neighborhoodId: string,
    houseId: string
  ): Promise<AdminHouseStreetMatchResult>;
  resetDevelopmentData(
    adminKey: string,
    neighborhoodId: string,
    confirmation: string
  ): Promise<AdminDevelopmentResetResult>;
  rebuildDevelopmentNeighborhood(
    adminKey: string,
    neighborhoodId: string,
    confirmation: string
  ): Promise<AdminRebuildResult>;
  publishNeighborhood(adminKey: string, neighborhoodId: string): Promise<AdminPublishResult>;
}

class HttpCoverageApi implements CoverageApi {
  constructor(private readonly baseUrl: string) {}

  async findIdentityCandidates(lastFour: string, portal: 'USER' | 'LEADER' | 'ADMIN'): Promise<IdentityCandidateResponse> {
    return this.request<IdentityCandidateResponse>('/v1/identity/candidates', {
      method: 'POST',
      body: JSON.stringify({ lastFour, portal })
    });
  }

  async startIdentityVerification(candidateToken: string, personId: string): Promise<IdentityVerificationStartResponse> {
    return this.request<IdentityVerificationStartResponse>('/v1/identity/verification/start', {
      method: 'POST',
      body: JSON.stringify({ candidateToken, personId })
    });
  }

  async startSelfRegistration(candidateToken: string, displayName: string, phoneNumber: string): Promise<IdentityVerificationStartResponse> {
    return this.request<IdentityVerificationStartResponse>('/v1/identity/registration/start', {
      method: 'POST', body: JSON.stringify({ candidateToken, displayName, phoneNumber })
    });
  }

  async verifyIdentityCode(verificationToken: string, code: string): Promise<IdentitySessionResponse> {
    return this.request<IdentitySessionResponse>('/v1/identity/verification/verify', {
      method: 'POST',
      body: JSON.stringify({ verificationToken, code })
    });
  }

  async getVolunteerBootstrap(): Promise<VolunteerBootstrap> {
    return this.request<VolunteerBootstrap>('/v1/volunteer/bootstrap', { method: 'GET' });
  }

  async getVolunteerAvailableSections(sessionToken: string, neighborhoodId: string): Promise<VolunteerAvailableSection[]> {
    return this.request<VolunteerAvailableSection[]>(
      `/v1/volunteer/neighborhoods/${encodeURIComponent(neighborhoodId)}/sections`,
      { method: 'GET', headers: { Authorization: `Bearer ${sessionToken}` } }
    );
  }

  async searchPlanningCenterPeople(sessionToken: string, query: string): Promise<PersonSearchResult[]> {
    return this.request<PersonSearchResult[]>(`/v1/people/search?query=${encodeURIComponent(query)}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${sessionToken}` }
    });
  }

  async getMyAssignments(sessionToken: string): Promise<AssignmentSummary[]> {
    return this.request<AssignmentSummary[]>('/v1/assignments/mine', {
      method: 'GET',
      headers: { Authorization: `Bearer ${sessionToken}` }
    });
  }

  async claimAssignment(request: ClaimAssignmentRequest): Promise<AssignmentSummary> {
    const result = await this.request<AssignmentSummary>('/v1/assignments/claim', {
      method: 'POST',
      body: JSON.stringify(request)
    });
    signalCoverageDataChanged();
    return result;
  }

  async getAssignmentSession(claimToken: string): Promise<AssignmentSummary> {
    return this.request<AssignmentSummary>('/v1/assignments/session', {
      method: 'GET',
      headers: { 'X-Claim-Token': claimToken }
    });
  }

  async startAssignmentSection(
    assignmentId: string,
    sectionId: string,
    claimToken: string,
    teamMembers: TeamMember[]
  ): Promise<AssignmentSummary> {
    const result = await this.request<AssignmentSummary>(
      `/v1/assignments/${encodeURIComponent(assignmentId)}/sections/${encodeURIComponent(sectionId)}/start`,
      { method: 'POST', headers: { 'X-Claim-Token': claimToken }, body: JSON.stringify({ teamMembers }) }
    );
    signalCoverageDataChanged();
    return result;
  }

  async completeAssignmentSection(
    assignmentId: string,
    sectionId: string,
    claimToken: string,
    teamMembers: TeamMember[],
    completionComment?: string
  ): Promise<AssignmentSummary> {
    const result = await this.request<AssignmentSummary>(
      `/v1/assignments/${encodeURIComponent(assignmentId)}/sections/${encodeURIComponent(sectionId)}/complete`,
      { method: 'POST', headers: { 'X-Claim-Token': claimToken }, body: JSON.stringify({ teamMembers, completionComment }) }
    );
    signalCoverageDataChanged();
    return result;
  }

  async reportHouseCondition(
    assignmentId: string,
    sectionId: string,
    houseId: string,
    claimToken: string,
    conditionStatus: HouseConditionStatus | null
  ): Promise<AssignmentSummary> {
    const result = await this.request<AssignmentSummary>(
      `/v1/assignments/${encodeURIComponent(assignmentId)}/sections/${encodeURIComponent(sectionId)}/houses/${encodeURIComponent(houseId)}/report`,
      {
        method: 'POST',
        headers: { 'X-Claim-Token': claimToken },
        body: JSON.stringify({ conditionStatus })
      }
    );
    signalCoverageDataChanged();
    return result;
  }

  async getLeaderDashboard(sessionToken: string): Promise<LeaderDashboardResponse> {
    return this.request<LeaderDashboardResponse>('/v1/leader/dashboard', {
      method: 'GET',
      headers: { Authorization: `Bearer ${sessionToken}` }
    });
  }

  async getLeaderCoverage(sessionToken: string): Promise<AdminCoverageDashboard> {
    return this.request<AdminCoverageDashboard>('/v1/leader/coverage', {
      method: 'GET',
      headers: { Authorization: `Bearer ${sessionToken}` }
    });
  }

  async getLeaderGroupMembers(sessionToken: string, groupId: string): Promise<GroupMemberSummary[]> {
    return this.request<GroupMemberSummary[]>(`/v1/leader/groups/${encodeURIComponent(groupId)}/members`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${sessionToken}` }
    });
  }

  async getLeaderReservedSections(sessionToken: string, groupId: string): Promise<LeaderAvailableSection[]> {
    return this.request<LeaderAvailableSection[]>(`/v1/leader/groups/${encodeURIComponent(groupId)}/sections`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${sessionToken}` }
    });
  }

  async getLeaderAvailableSections(sessionToken: string, neighborhoodId: string, groupId: string): Promise<LeaderAvailableSection[]> {
    return this.request<LeaderAvailableSection[]>(`/v1/leader/neighborhoods/${encodeURIComponent(neighborhoodId)}/sections?groupId=${encodeURIComponent(groupId)}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${sessionToken}` }
    });
  }

  async assignLeaderMaps(
    sessionToken: string,
    input: { groupId: string; personId: string; neighborhoodId: string; requestedStacks: number; selectedSectionIds: string[]; allowAdditional: boolean }
  ): Promise<AssignmentSummary> {
    const result = await this.request<AssignmentSummary>('/v1/leader/assignments', {
      method: 'POST',
      headers: { Authorization: `Bearer ${sessionToken}` },
      body: JSON.stringify(input)
    });
    signalCoverageDataChanged();
    return result;
  }

  async releaseLeaderSections(sessionToken: string, assignmentId: string, routeIds: string[]): Promise<SectionReleaseResult> {
    const result = await this.request<SectionReleaseResult>(`/v1/leader/assignments/${encodeURIComponent(assignmentId)}/release-sections`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${sessionToken}` },
      body: JSON.stringify({ routeIds })
    });
    signalCoverageDataChanged();
    return result;
  }

  async getPlanningCenterStatus(adminKey: string): Promise<PlanningCenterSyncStatus> {
    return this.adminRequest<PlanningCenterSyncStatus>('/v1/admin/pco/status', adminKey, { method: 'GET' });
  }

  async getSelfRegistrations(adminKey: string): Promise<SelfRegisteredPersonSummary[]> {
    return this.adminRequest<SelfRegisteredPersonSummary[]>('/v1/admin/self-registrations', adminKey, { method: 'GET' });
  }

  async markSelfRegistrationReviewed(adminKey: string, personId: string): Promise<void> {
    await this.adminRequest(`/v1/admin/self-registrations/${encodeURIComponent(personId)}/review`, adminKey, { method: 'POST', body: '{}' });
  }

  async syncPlanningCenter(adminKey: string): Promise<PlanningCenterSyncResult> {
    return this.adminRequest<PlanningCenterSyncResult>('/v1/admin/pco/sync', adminKey, {
      method: 'POST',
      body: '{}'
    });
  }

  async searchAdminPeople(adminKey: string, query: string): Promise<PersonSearchResult[]> {
    return this.adminRequest<PersonSearchResult[]>(`/v1/admin/people/search?query=${encodeURIComponent(query)}`, adminKey, { method: 'GET' });
  }

  async searchAdminAccessPeople(adminKey: string, query: string): Promise<AccessManagementPerson[]> {
    return this.adminRequest<AccessManagementPerson[]>(`/v1/admin/access/search?query=${encodeURIComponent(query)}`, adminKey, { method: 'GET' });
  }

  async setAdminAccess(adminKey: string, personId: string, role: 'LEADER' | 'ADMIN', enabled: boolean): Promise<AccessChangeResult> {
    return this.adminRequest<AccessChangeResult>(`/v1/admin/access/${encodeURIComponent(personId)}`, adminKey, { method: 'PATCH', body: JSON.stringify({ role, enabled }) });
  }

  async searchAdminGroups(adminKey: string, query: string): Promise<GroupSearchResult[]> {
    return this.adminRequest<GroupSearchResult[]>(`/v1/admin/groups/search?query=${encodeURIComponent(query)}`, adminKey, { method: 'GET' });
  }

  async reserveAdminSectionsForGroup(adminKey: string, groupId: string, selectedSectionIds: string[]): Promise<GroupReservationResult> {
    const result = await this.adminRequest<GroupReservationResult>('/v1/admin/group-reservations', adminKey, { method: 'POST', body: JSON.stringify({ groupId, selectedSectionIds }) });
    signalCoverageDataChanged();
    return result;
  }

  async releaseAdminGroupReservations(adminKey: string, routeIds: string[]): Promise<GroupReservationResult> {
    const result = await this.adminRequest<GroupReservationResult>('/v1/admin/group-reservations/release', adminKey, { method: 'POST', body: JSON.stringify({ routeIds }) });
    signalCoverageDataChanged();
    return result;
  }

  async assignAdminMaps(
    adminKey: string,
    input: { personId: string; neighborhoodId: string; requestedStacks: number; selectedSectionIds: string[]; allowAdditional: boolean }
  ): Promise<AssignmentSummary> {
    const result = await this.adminRequest<AssignmentSummary>('/v1/admin/assignments', adminKey, {
      method: 'POST',
      body: JSON.stringify(input)
    });
    signalCoverageDataChanged();
    return result;
  }

  async releaseAdminSections(adminKey: string, assignmentId: string, routeIds: string[]): Promise<SectionReleaseResult> {
    const result = await this.adminRequest<SectionReleaseResult>(`/v1/admin/assignments/${encodeURIComponent(assignmentId)}/release-sections`, adminKey, {
      method: 'POST',
      body: JSON.stringify({ routeIds })
    });
    signalCoverageDataChanged();
    return result;
  }

  async returnAdminAssignmentToGroup(adminKey: string, assignmentId: string): Promise<GroupAssignmentReturnResult> {
    const result = await this.adminRequest<GroupAssignmentReturnResult>(
      `/v1/admin/assignments/${encodeURIComponent(assignmentId)}/return-to-group`,
      adminKey,
      { method: 'POST', body: '{}' }
    );
    signalCoverageDataChanged();
    return result;
  }

  async completeAdminSection(adminKey: string, sectionId: string, teamMembers: TeamMember[]): Promise<void> {
    await this.adminRequest(`/v1/admin/sections/${encodeURIComponent(sectionId)}/complete`, adminKey, { method: 'POST', body: JSON.stringify({ teamMembers }) });
    signalCoverageDataChanged();
  }

  async reopenAdminUnassignedSection(adminKey: string, sectionId: string): Promise<void> {
    await this.adminRequest(`/v1/admin/sections/${encodeURIComponent(sectionId)}/reopen`, adminKey, { method: 'POST', body: '{}' });
    signalCoverageDataChanged();
  }

  async reopenAdminSection(adminKey: string, assignmentId: string, sectionId: string): Promise<AssignmentSummary> {
    const result = await this.adminRequest<AssignmentSummary>(
      `/v1/admin/assignments/${encodeURIComponent(assignmentId)}/sections/${encodeURIComponent(sectionId)}/reopen`,
      adminKey,
      { method: 'POST', body: '{}' }
    );
    signalCoverageDataChanged();
    return result;
  }

  async getAdminNeighborhoods(adminKey: string): Promise<AdminNeighborhoodSummary[]> {
    return this.adminRequest<AdminNeighborhoodSummary[]>('/v1/admin/neighborhoods', adminKey, { method: 'GET' });
  }

  async setNeighborhoodPlanningGroup(
    adminKey: string,
    neighborhoodId: string,
    planningGroup: 'CURRENT' | 'FUTURE' | 'NOT_IN_SCOPE'
  ): Promise<AdminNeighborhoodSummary> {
    return this.adminRequest<AdminNeighborhoodSummary>(
      `/v1/admin/neighborhoods/${encodeURIComponent(neighborhoodId)}/planning-group`,
      adminKey,
      { method: 'PATCH', body: JSON.stringify({ planningGroup }) }
    );
  }

  async recheckNeighborhood(adminKey: string, neighborhoodId: string): Promise<NeighborhoodRecheckResult> {
    return this.adminRequest<NeighborhoodRecheckResult>(
      `/v1/admin/neighborhoods/${encodeURIComponent(neighborhoodId)}/recheck`,
      adminKey,
      { method: 'POST', body: '{}' }
    );
  }

  async getSeason(adminKey: string): Promise<SeasonState> {
    return this.adminRequest<SeasonState>('/v1/admin/season', adminKey, { method: 'GET' });
  }

  async pauseSeason(adminKey: string): Promise<SeasonActionResult> {
    const result = await this.adminRequest<SeasonActionResult>('/v1/admin/season/pause', adminKey, { method: 'POST', body: '{}' });
    signalCoverageDataChanged();
    return result;
  }

  async resumeSeason(adminKey: string): Promise<SeasonActionResult> {
    const result = await this.adminRequest<SeasonActionResult>('/v1/admin/season/resume', adminKey, { method: 'POST', body: '{}' });
    signalCoverageDataChanged();
    return result;
  }

  async closeSeason(adminKey: string, confirmation: string): Promise<SeasonActionResult> {
    const result = await this.adminRequest<SeasonActionResult>('/v1/admin/season/stop', adminKey, {
      method: 'POST', body: JSON.stringify({ confirmation })
    });
    signalCoverageDataChanged();
    return result;
  }

  async createSeason(adminKey: string, name: string): Promise<SeasonActionResult> {
    const result = await this.adminRequest<SeasonActionResult>('/v1/admin/season/start', adminKey, {
      method: 'POST', body: JSON.stringify({ name })
    });
    signalCoverageDataChanged();
    return result;
  }

  async clearAssignmentActivity(adminKey: string, confirmation: string): Promise<AssignmentActivityResetResult> {
    const result = await this.adminRequest<AssignmentActivityResetResult>('/v1/admin/testing/clear-activity', adminKey, {
      method: 'POST',
      body: JSON.stringify({ confirmation })
    });
    signalCoverageDataChanged();
    return result;
  }

  async getAdminCoverage(adminKey: string, neighborhoodId?: string): Promise<AdminCoverageDashboard> {
    const path = neighborhoodId
      ? `/v1/admin/neighborhoods/${encodeURIComponent(neighborhoodId)}/coverage`
      : '/v1/admin/coverage';
    return this.adminRequest<AdminCoverageDashboard>(path, adminKey, { method: 'GET' });
  }

  async getAdminReview(adminKey: string, neighborhoodId: string): Promise<AdminNeighborhoodReview> {
    return this.adminRequest<AdminNeighborhoodReview>(
      `/v1/admin/neighborhoods/${encodeURIComponent(neighborhoodId)}/review`,
      adminKey,
      { method: 'GET' }
    );
  }

  async setAdminHouseCondition(adminKey: string, neighborhoodId: string, houseId: string, conditionStatus: HouseConditionStatus | null): Promise<ReviewHouse> {
    const result = await this.adminRequest<ReviewHouse>(
      `/v1/admin/neighborhoods/${encodeURIComponent(neighborhoodId)}/houses/${encodeURIComponent(houseId)}/condition`,
      adminKey,
      { method: 'PATCH', body: JSON.stringify({ conditionStatus }) }
    );
    signalCoverageDataChanged();
    return result;
  }

  async setHouseInclusion(
    adminKey: string,
    neighborhoodId: string,
    houseId: string,
    inclusionStatus: HouseInclusionStatus
  ): Promise<ReviewHouse> {
    return this.adminRequest<ReviewHouse>(
      `/v1/admin/neighborhoods/${encodeURIComponent(neighborhoodId)}/houses/${encodeURIComponent(houseId)}`,
      adminKey,
      { method: 'PATCH', body: JSON.stringify({ inclusionStatus }) }
    );
  }

  async setHousesInclusion(
    adminKey: string,
    neighborhoodId: string,
    houseIds: string[],
    inclusionStatus: HouseInclusionStatus
  ): Promise<ReviewHouse[]> {
    return this.adminRequest<ReviewHouse[]>(
      `/v1/admin/neighborhoods/${encodeURIComponent(neighborhoodId)}/houses`,
      adminKey,
      { method: 'PATCH', body: JSON.stringify({ houseIds, inclusionStatus }) }
    );
  }

  async matchHouseToStreet(
    adminKey: string,
    neighborhoodId: string,
    houseId: string,
    streetUnitId: string,
    method: 'ADMIN_SUGGESTED' | 'ADMIN_SELECTED'
  ): Promise<AdminHouseStreetMatchResult> {
    return this.adminRequest<AdminHouseStreetMatchResult>(
      `/v1/admin/neighborhoods/${encodeURIComponent(neighborhoodId)}/houses/${encodeURIComponent(houseId)}/street-match`,
      adminKey,
      { method: 'PATCH', body: JSON.stringify({ streetUnitId, method }) }
    );
  }

  async createManualStreetForHouse(
    adminKey: string,
    neighborhoodId: string,
    houseId: string
  ): Promise<AdminHouseStreetMatchResult> {
    return this.adminRequest<AdminHouseStreetMatchResult>(
      `/v1/admin/neighborhoods/${encodeURIComponent(neighborhoodId)}/houses/${encodeURIComponent(houseId)}/manual-street`,
      adminKey,
      { method: 'POST', body: '{}' }
    );
  }

  async resetDevelopmentData(
    adminKey: string,
    neighborhoodId: string,
    confirmation: string
  ): Promise<AdminDevelopmentResetResult> {
    const result = await this.adminRequest<AdminDevelopmentResetResult>(
      `/v1/admin/neighborhoods/${encodeURIComponent(neighborhoodId)}/reset-development`,
      adminKey,
      { method: 'POST', body: JSON.stringify({ confirmation }) }
    );
    signalCoverageDataChanged();
    return result;
  }

  async rebuildDevelopmentNeighborhood(
    adminKey: string,
    neighborhoodId: string,
    confirmation: string
  ): Promise<AdminRebuildResult> {
    const result = await this.adminRequest<AdminRebuildResult>(
      `/v1/admin/neighborhoods/${encodeURIComponent(neighborhoodId)}/rebuild-development`,
      adminKey,
      { method: 'POST', body: JSON.stringify({ confirmation }) }
    );
    signalCoverageDataChanged();
    return result;
  }

  async publishNeighborhood(adminKey: string, neighborhoodId: string): Promise<AdminPublishResult> {
    const result = await this.adminRequest<AdminPublishResult>(
      `/v1/admin/neighborhoods/${encodeURIComponent(neighborhoodId)}/publish`,
      adminKey,
      { method: 'POST', body: '{}' }
    );
    signalCoverageDataChanged();
    return result;
  }

  private adminRequest<T>(path: string, adminKey: string, init: RequestInit): Promise<T> {
    return this.request<T>(path, {
      ...init,
      headers: { Authorization: `Bearer ${adminKey}`, ...(init.headers ?? {}) }
    });
  }

  private async request<T>(path: string, init: RequestInit): Promise<T> {
    const requestId = crypto.randomUUID();
    const response = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Request-ID': requestId,
        ...(init.headers ?? {})
      }
    });

    if (!response.ok) {
      let payload: ApiErrorPayload = { message: `Request failed (${response.status})` };
      try {
        payload = (await response.json()) as ApiErrorPayload;
      } catch {
        // Keep the status-based message when the response is not JSON.
      }
      throw new CoverageApiError(payload.message, payload.code, payload.requestId, response.status);
    }

    return (await response.json()) as T;
  }
}

export function createCoverageApi(): CoverageApi {
  // Firebase Hosting rewrites /api/** to the Cloud Run API. Missing build-time
  // environment variables must NEVER activate the mock SMS or assignment API.
  // Mock data is permitted only in an explicitly requested local dev session.
  const baseUrl = String(import.meta.env.VITE_API_BASE_URL ?? '').trim().replace(/\/$/, '');
  if (import.meta.env.DEV && import.meta.env.VITE_APP_ENV === 'mock') {
    return new MockCoverageApi();
  }
  return new HttpCoverageApi(baseUrl || '/api');
}
